import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { UsClientError, UsTradingClient, type UsClientSession } from './tradingClient';

/** Construct only when mounting a US ticket. Does not register a route, open a
 * connection, poll, refresh credentials or alter the other venue clients. */
export function createUsBrowserTradingClient(): UsTradingClient {
  // Defer storage access: a blocked browser store must not crash construction
  // or reads. Submission checks persistence before issuing any write.
  const storage = { get length() { return window.localStorage.length; },
    key: (index: number) => window.localStorage.key(index), getItem: (key: string) => window.localStorage.getItem(key),
    setItem: (key: string, value: string) => window.localStorage.setItem(key, value), removeItem: (key: string) => window.localStorage.removeItem(key) };
  return new UsTradingClient({ session: usBrowserSession, storage,
    uuid: () => crypto.randomUUID(), clock: Date.now,
    send: sendUsBrowserRequest,
  });
}

export const usBrowserSession = (): UsClientSession | null => {
  const s = useAuthStore.getState();
  return s.status === 'authenticated' && s.user?.orgId && s.token
    ? { userId: s.user.id, orgId: s.user.orgId, generation: getAuthSessionEpoch() } : null;
};

export async function sendUsBrowserRequest(path: string, init: RequestInit, expected: UsClientSession): Promise<Response> {
  const current = usBrowserSession();
  if (!current || current.userId !== expected.userId || current.orgId !== expected.orgId
    || current.generation !== expected.generation) throw new UsClientError('US_CLIENT_CONTEXT_CHANGED');
  const token = useAuthStore.getState().token;
  // #1007 adds exactly the bounded fills read and #1031 exactly My Trades' GET-only own-history read
  // (no query); the account-free market reads (catalogue and one market's depth) are GET-only.
  // Nothing else under /api/polymarket-us.
  const publicMarketRead = (init.method ?? 'GET') === 'GET'
    && (/^\/api\/polymarket-us\/markets(?:\?[^#/]*)?$/.test(path)
      || /^\/api\/polymarket-us\/markets\/[A-Za-z0-9_-]{1,256}\/book$/.test(path));
  const allowed = path === '/api/polymarket-us/accounts' || path.startsWith('/api/polymarket-us/accounts?')
    || path.startsWith('/api/polymarket-us/accounts/')
    || path === '/api/polymarket-us/fills' || path.startsWith('/api/polymarket-us/fills?')
    // #1031: My Trades' own-history read; it takes no parameters.
    || ((init.method ?? 'GET') === 'GET' && path === '/api/polymarket-us/my-trades')
    || publicMarketRead;
  if (!token || !allowed) throw new UsClientError('US_CLIENT_AUTH_REQUIRED');
  const headers = new Headers(init.headers); headers.set('Authorization', `Bearer ${token}`);
  // apiFetch retries on 401. A US write uses one transport invocation;
  // refresh/login and explicit read-only recovery are separate actions.
  return fetch(path, { ...init, headers, redirect: 'error' });
}
